import { SHERBERT_ASSETS_API_URL_V3 } from "../urls.js";
import { QueryBuilder } from "../utils/QueryBuilder.js";
import { FormDataBuilder } from "../utils/FormDataBuilder.js";
import { xhrSherbertFetch, } from "./networkUtils/xhrSherbertFetch.js";
import { preferHeader } from "./networkUtils/preferHeader.js";
const getFormData = (body) => FormDataBuilder()
    .add("file", body.file)
    .add("name", body.name)
    .add("versionLabel", body.versionLabel)
    .add("type", body.type)
    .add("tenant", body.tenant)
    .add("expires", body.expires)
    .add("tags", body.tags)
    .add("properties", body.properties)
    .add("info", body.info)
    .add("createNewVersion", body.createNewVersion)
    .add("skipVariants", body.skipVariants)
    .add("includeVariants", body.includeVariants)
    .add("folderId", body.folderId)
    .add("links", body.links)
    .toFormData();
/**
 * Create a new asset
 * @param options - The options for creating an asset
 * @param options.body - The body of the asset to create
 * @param options.authorizationHeader - The authorization header to use for the request
 * @param options.parameters - [optional] The parameters for the asset to create
 * @param options.abortSignal - [optional] The abort signal to use for the request
 * @param options.onProgress - [optional] The progress callback to use for the request
 * @param options.readOnlyPresignOnCreate - [optional] Where to return the signature in the response
 * @returns The created asset
 * @example
 * ```ts
 * const asset = await createAsset({
 *   body: { file: new File([], "test.png") },
 *   authorizationHeader: () => `Bearer ${token}`,
 *   onProgress: (progress) => {
 *     console.log(`Uploaded ${progress.loaded} bytes of ${progress.total} bytes`);
 *   },
 * });
 * ```
 */
export async function createAsset({ body, parameters = {}, authorizationHeader, abortSignal, onProgress, readOnlyPresignOnCreate, }) {
    const formData = getFormData(body);
    const query = QueryBuilder()
        .add("scopeToUserFolders", parameters.scopeToUserFolders)
        .add("signature", parameters.signature)
        .add("folderName", parameters.folderName)
        .add("readOnlyPresignOnCreate", readOnlyPresignOnCreate)
        .toString();
    const response = await xhrSherbertFetch(() => [
        `${SHERBERT_ASSETS_API_URL_V3}${query ? `?${query}` : ""}`,
        {
            method: "POST",
            headers: {
                Authorization: authorizationHeader(),
                Accept: "application/json",
                ...preferHeader(),
            },
            body: formData,
            signal: abortSignal,
        },
    ], { onProgress });
    const data = await response.json();
    abortSignal?.throwIfAborted();
    if (response.status === 201) {
        return data;
    }
    else {
        // Other errors non ok reponses should have been handled by sherbertFetch
        throw new Error("Failed to create asset. Unexpected error");
    }
}
/**
 * Create a new asset from a presigned URL
 * @param options - The options for creating an asset from a presigned URL
 * @param options.presignedUrl - The presigned URL to use for the asset
 * @param options.body - The body of the asset to create
 * @param options.abortSignal - [optional] The abort signal to use for the request
 * @param options.onProgress - [optional] The progress callback to use for the request
 * @returns The created asset
 * @example
 * ```ts
 * const presignedUrl = await getPresignedUrl({
 *   authorizationHeader: () => `Bearer ${token}`,
 * });
 * const asset = await createAssetFromPresignedUrl({
 *   presignedUrl: presignedUrl._links.self.href,
 *   body: { file: new File([], "test.png") },
 *   onProgress: (progress) => {
 *     console.log(`Uploaded ${progress.loaded} bytes of ${progress.total} bytes`);
 *   },
 * });
 * ```
 */
export async function createAssetFromPresignedUrl({ presignedUrl, body, abortSignal, onProgress, }) {
    const formData = getFormData(body);
    const response = await xhrSherbertFetch(() => [
        presignedUrl,
        {
            method: "POST",
            body: formData,
            signal: abortSignal,
            headers: {
                Accept: "application/json",
                ...preferHeader(),
            },
        },
    ], { onProgress });
    if (response.status === 201) {
        const data = await response.json();
        abortSignal?.throwIfAborted();
        return data;
    }
    // Other errors non ok reponses should have been handled by sherbertFetch
    throw new Error("Failed to create asset. Unexpected error");
}
